import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
} from 'react';

import type { Channel } from 'belo-js-sdk';

import type { DefaultBeloChatGenerics } from '../types/types';

export type ChannelListContextValue<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
> = {
  /**
   * State representing the array of loaded channels.
   * Channels query is executed by default only by ChannelList component in the SDK.
   */
  channels: Channel<BeloChatGenerics>[];
  /**
   * Sets the list of Channel objects to be rendered by ChannelList component.
   */
  setChannels: Dispatch<SetStateAction<Channel<BeloChatGenerics>[]>>;
};

export const ChannelListContext = createContext<ChannelListContextValue | undefined>(undefined);

/**
 * Context provider for components rendered within the `ChannelList`
 */
export const ChannelListContextProvider = <
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
>({
  children,
  value,
}: PropsWithChildren<{
  value: ChannelListContextValue<BeloChatGenerics>;
}>) => (
  <ChannelListContext.Provider value={(value as unknown) as ChannelListContextValue}>
    {children}
  </ChannelListContext.Provider>
);

export const useChannelListContext = <
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
>(
  componentName?: string,
) => {
  const contextValue = useContext(ChannelListContext);

  if (!contextValue) {
    console.warn(
      `The useChannelListContext hook was called outside of the ChannelListContext provider. Make sure this hook is called within the ChannelList component. The errored call is located in the ${componentName} component.`,
    );

    return {} as ChannelListContextValue<BeloChatGenerics>;
  }

  return (contextValue as unknown) as ChannelListContextValue<BeloChatGenerics>;
};
